import { FaRedo } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';

/**
 * Shown when the user is signed in but users/{uid} could not be read.
 *
 * Previously only SecretBackendLogin handled this. Everywhere else a failed profile
 * read looked exactly like "this user has no document": Login's redirect effect
 * early-returned on `!profile` so nothing happened after a successful sign-in, and
 * RoleGuard sent the user to /login, which could not redirect them onward — a loop
 * that reads to the user as "login is broken".
 *
 * AuthContext now distinguishes the two and retries once with a forced token refresh
 * before rendering this, so by the time anyone sees this card the obvious cause has
 * already been ruled out.
 */
export default function ProfileUnavailable() {
  const { profileError, retryProfile, profileLoading, logout } = useAuth();

  const denied = profileError === 'permission-denied';

  return (
    <div className="h-[100dvh] w-screen overflow-hidden flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4">
      <Card className="w-full max-w-md border-amber-500/30 bg-card/95 shadow-xl">
        <CardHeader>
          <CardTitle className="text-lg text-amber-600 dark:text-amber-400">
            Profile could not be loaded
          </CardTitle>
          <CardDescription className="text-left text-sm text-muted-foreground space-y-2">
            {denied ? (
              <>
                <p>
                  You are signed in, but this account does not currently have permission to
                  read its own profile. We already refreshed your session once and it did not
                  help.
                </p>
                <p>
                  This usually means your account is mid-setup. If it persists, your school
                  administrator or the In3D.ai team can check the account.
                </p>
              </>
            ) : (
              <>
                <p>
                  You are signed in, but your profile could not be loaded. This often happens
                  when a browser extension blocks Firestore (ad blocker, privacy or cookie
                  blocker).
                </p>
                <p>
                  <strong>Try this:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Disable ad blockers or privacy extensions for this site</li>
                  <li>
                    Whitelist{' '}
                    <code className="text-xs bg-muted px-1 rounded">firestore.googleapis.com</code>
                  </li>
                  <li>Use an incognito/private window with extensions disabled</li>
                </ul>
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Button onClick={retryProfile} disabled={profileLoading}>
            {profileLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Loading...
              </span>
            ) : (
              <>
                <FaRedo className="w-4 h-4 mr-2" />
                Retry loading profile
              </>
            )}
          </Button>
          <Button variant="outline" onClick={() => logout()}>
            Sign out and try again
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
